import React, { FormEvent, useRef, useState } from "react";
import { Alert, Button, Card, ListGroup, Table } from "react-bootstrap";
import GenericModal from "../global/GenericModal/GenericModal";
import {
  SpitKitImportDNAFileResult,
  SpitKitValidationError,
} from "src/store/SpitKit/Types";
import { useInjection } from "src/services/ServicesContext";
import { ISpitKitService } from "src/services/SpitKitService";

type SpitKitValidationErrorsProps = {
  errors: SpitKitValidationError[];
  title: string;
};

function SpitKitValidationErrors({
  errors,
  title,
}: SpitKitValidationErrorsProps) {
  if (!errors || errors.length === 0) {
    return null;
  }

  return (
    <Card className="mb-2">
      <Card.Header className="text-danger">{title}</Card.Header>
      <Table striped bordered hover size="sm" className="mb-0">
        <thead>
          <tr>
            <th>Row</th>
            <th>Serial Number</th>
            <th>Current Status</th>
            <th>Requested Status</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {errors.map((error, index) => (
            <tr key={index}>
              <td>{error.row}</td>
              <td>{error.serial_number}</td>
              <td>{error.current_status || "-"}</td>
              <td>{error.requested_status || "-"}</td>
              <td>{error.error}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

export type SpitKitImportDNAFileModalProps = {
  show?: boolean;
  onCancel?: () => void;
  onFinish?: () => void;
};

function SpitKitImportDNAFileModal({
  show = false,
  onCancel = () => {},
  onFinish = () => {},
}: SpitKitImportDNAFileModalProps) {
  const [file, setFile] = useState<File>();
  const [isProceeding, setIsProceeding] = useState<boolean>(false);
  const [errors, setErrors] = useState<Error[]>([]);
  const [spitKitImportResult, setSpitKitImportResult] =
    useState<SpitKitImportDNAFileResult>();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const spitKitService = useInjection<ISpitKitService>("spitKitService");

  const handleFileChanged = ({
    currentTarget,
  }: FormEvent<HTMLInputElement>) => {
    setErrors([]);
    setFile((currentTarget.files || [])[0]);
  };

  const resetState = () => {
    setErrors([]);
    setSpitKitImportResult(undefined);
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  const handleAlternate = () => {
    resetState();
    onFinish();
  };

  const handleError = (error: any) => {
    const responseErrors = error?.response?.data?.validation_errors;

    if (responseErrors) {
      setSpitKitImportResult(error.response.data as SpitKitImportDNAFileResult);
    } else {
      setErrors([error instanceof Error ? error : new Error(String(error))]);
    }
  };

  const handleContinue = async () => {
    if (!file) return;

    setIsProceeding(true);
    setErrors([]);

    try {
      const result = await spitKitService.importSpitKitDNAFile(file);
      setSpitKitImportResult(result);
    } catch (error) {
      handleError(error);
    } finally {
      setIsProceeding(false);
      setFile(undefined);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const err = Boolean(spitKitImportResult?.validation_errors?.length);
  const succeeded = !errors.length && spitKitImportResult?.success && !err;
  const results = Boolean(errors.length || spitKitImportResult);

  return (
    <GenericModal
      title={"Upload DNA Lab file"}
      show={show}
      onCancel={handleCancel}
      onContinue={handleContinue}
      canProceed={!!file}
      proceeding={isProceeding}
      isAlternate={results}
      alternateAction={<Button onClick={handleAlternate}>Ok</Button>}
    >
      <div className="mb-3">
        <p>
          Use this feature to update orders that have been received for the
          purpose of setting the Kit order status according to the following
          table:
        </p>
        <Table striped bordered hover size="sm">
          <thead className="thead-dark">
            <tr>
              <th>Current Kit Status</th>
              <th>File Status</th>
              <th>New Status</th>
              <th>Previous Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>returned</td>
              <td>to_rgc</td>
              <td>to_rgc</td>
              <td>returned</td>
            </tr>
            <tr>
              <td>to_rgc</td>
              <td>sequenced</td>
              <td>sequenced</td>
              <td>to_rgc</td>
            </tr>
            <tr>
              <td>any status</td>
              <td>qc_failed</td>
              <td>qc_failed</td>
              <td>(unchanged)</td>
            </tr>
          </tbody>
        </Table>
      </div>
      <input
        className="form-control-file mb-2"
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileChanged}
      />
      <>
        {succeeded && (
          <Alert variant="success">
            DNA file has been processed successfully
            {spitKitImportResult?.data && (
              <div className="mt-2">
                <strong>Total processed rows:</strong>{" "}
                {spitKitImportResult.data.total_processed_rows}
                <br />
                <strong>Successful updates:</strong>{" "}
                {spitKitImportResult.data.total_successful_updates}
              </div>
            )}
          </Alert>
        )}

        {err && (
          <>
            <Alert variant="danger">
              {spitKitImportResult?.msg || "DNA file validation failed"}
            </Alert>
            <SpitKitValidationErrors
              errors={spitKitImportResult?.validation_errors || []}
              title={`Validation Errors (${spitKitImportResult?.total_errors || spitKitImportResult?.validation_errors?.length || 0})`}
            />
          </>
        )}

        {errors.length > 0 && (
          <ListGroup className="mt-3">
            {errors.map((e, index) => (
              <ListGroup.Item key={index} className="text-danger">
                {e.message}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </>
    </GenericModal>
  );
}

export default SpitKitImportDNAFileModal;
